import { morph } from '@theme/morph';
import { Component } from '@theme/component';
import { StandardEvents, ProductSelectEvent } from '@shopify/events';

const MS_PER_DAY = 86_400_000;
const DEFAULT_LEAD_MIN = 2;
const DEFAULT_LEAD_MAX = 5;

const CONFIG_KEYS = [
  'productId',
  'available',
  'leadMin',
  'leadMax',
  'enableCutoff',
  'showCountdown',
  'skipWeekends',
  'cutoffTimestamp',
  'messages',
];

/**
 * Fills date / countdown copy after comparing Date.now() to the store cutoff
 * epoch. Stays hidden until the first paint (strict — no Liquid date flash).
 *
 * @extends {Component}
 */
class ProductDeliveryEstimate extends Component {
  /** @type {ReturnType<typeof setInterval> | null} */
  #countdownInterval = null;

  /** @type {Intl.DateTimeFormat | null} */
  #dateFormatter = null;

  /** @type {Record<string, string> | null} */
  #messages = null;

  connectedCallback() {
    super.connectedCallback();
    const closestSection = this.closest('.shopify-section, dialog');
    closestSection?.addEventListener(StandardEvents.productSelect, this.#onProductSelect);
    this.#renderEstimate();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    const closestSection = this.closest('.shopify-section, dialog');
    closestSection?.removeEventListener(StandardEvents.productSelect, this.#onProductSelect);
    this.#stopCountdown();
  }

  /** @param {ProductSelectEvent} event */
  #onProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;
        this.updateDeliveryEstimate(detail);
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[product-delivery-estimate] Event promise rejected:', error);
      });
  };

  /**
   * @param {NonNullable<Awaited<ProductSelectEvent['promise']>['detail']>} detail
   */
  updateDeliveryEstimate = (detail) => {
    const { html, newProduct } = detail;

    if (newProduct) {
      this.dataset.productId = newProduct.id;
    } else if (detail.productId && detail.productId !== this.dataset.productId) {
      return;
    }

    const newEstimate = html.querySelector('product-delivery-estimate');
    if (!newEstimate) return;

    this.#copyConfigAttributes(newEstimate);
    morph(this, newEstimate, { childrenOnly: true });
    this.#renderEstimate();
  };

  /**
   * Host data-* survives childrenOnly morph — copy config from section HTML.
   * @param {Element} source
   */
  #copyConfigAttributes(source) {
    if (!(source instanceof HTMLElement)) return;

    this.#messages = null;

    for (const key of CONFIG_KEYS) {
      const value = source.dataset[key];

      if (value == null || value === '') {
        delete this.dataset[key];
      } else {
        this.dataset[key] = value;
      }
    }
  }

  #renderEstimate() {
    this.#stopCountdown();

    if (this.dataset.available !== 'true') {
      this.setAttribute('hidden', '');
      return;
    }

    const messageEl = this.querySelector('[data-delivery-estimate-message]');
    if (!(messageEl instanceof HTMLElement)) {
      this.setAttribute('hidden', '');
      return;
    }

    let leadMin = this.#parseLead(this.dataset.leadMin, DEFAULT_LEAD_MIN);
    let leadMax = this.#parseLead(this.dataset.leadMax, DEFAULT_LEAD_MAX);

    if (leadMax < leadMin) {
      [leadMin, leadMax] = [leadMax, leadMin];
    }

    const enableCutoff = this.dataset.enableCutoff === 'true';
    const cutoffTimestamp = Number(this.dataset.cutoffTimestamp);
    const hasCutoff = enableCutoff && Number.isFinite(cutoffTimestamp) && cutoffTimestamp > 0;
    const now = Date.now();
    const pastCutoff = hasCutoff && now >= cutoffTimestamp;
    const cutoffDays = pastCutoff ? 1 : 0;

    const fromDate = new Date(now + (leadMin + cutoffDays) * MS_PER_DAY);
    const toDate = new Date(now + (leadMax + cutoffDays) * MS_PER_DAY);

    if (this.dataset.skipWeekends === 'true') {
      this.#nudgeOffWeekend(fromDate);
      this.#nudgeOffWeekend(toDate);
    }

    const dateFrom = this.#formatDate(fromDate);
    const dateTo = this.#formatDate(toDate);
    const sameDay =
      fromDate.getFullYear() === toDate.getFullYear() &&
      fromDate.getMonth() === toDate.getMonth() &&
      fromDate.getDate() === toDate.getDate();

    const messageHtml = sameDay
      ? this.#fillTemplate(this.#getMessage('deliveryEstimateSingle'), { date: dateFrom })
      : this.#fillTemplate(this.#getMessage('deliveryEstimateRange'), {
          date_from: dateFrom,
          date_to: dateTo,
        });

    if (!messageHtml) {
      this.setAttribute('hidden', '');
      return;
    }

    messageEl.innerHTML = messageHtml;

    this.#syncCountdown(this.dataset.showCountdown === 'true' && hasCutoff && !pastCutoff, cutoffTimestamp);
    this.removeAttribute('hidden');
  }

  /**
   * @param {boolean} showCountdown
   * @param {number} cutoffTimestamp
   */
  #syncCountdown(showCountdown, cutoffTimestamp) {
    const countdownEl = this.querySelector('[data-delivery-countdown]');
    if (!(countdownEl instanceof HTMLElement)) return;

    if (!showCountdown) {
      countdownEl.textContent = '';
      countdownEl.hidden = true;
      return;
    }

    const update = () => {
      const msRemaining = cutoffTimestamp - Date.now();

      if (msRemaining <= 0) {
        this.#renderEstimate();
        return;
      }

      const totalMinutes = Math.floor(msRemaining / 60_000);
      const hours = Math.floor(totalMinutes / 60);
      const minutes = totalMinutes % 60;

      if (hours > 1) {
        countdownEl.textContent = this.#fillTemplate(this.#getMessage('deliveryEstimateCutoffHours'), {
          hours: String(hours),
        });
      } else if (hours === 1) {
        countdownEl.textContent = this.#getMessage('deliveryEstimateCutoffHour');
      } else if (minutes === 1) {
        countdownEl.textContent = this.#getMessage('deliveryEstimateCutoffMinute');
      } else {
        countdownEl.textContent = this.#fillTemplate(this.#getMessage('deliveryEstimateCutoffMinutes'), {
          minutes: String(Math.max(minutes, 1)),
        });
      }

      countdownEl.hidden = false;
    };

    update();
    this.#countdownInterval = setInterval(update, 60_000);
  }

  #stopCountdown() {
    if (this.#countdownInterval == null) return;

    clearInterval(this.#countdownInterval);
    this.#countdownInterval = null;
  }

  /**
   * @param {string | undefined} value
   * @param {number} fallback
   * @returns {number}
   */
  #parseLead(value, fallback) {
    if (value == null || value === '') return fallback;

    const parsed = Number.parseInt(String(value).trim(), 10);
    if (!Number.isFinite(parsed) || parsed < 0) return fallback;

    return parsed;
  }

  /** @param {Date} date */
  #nudgeOffWeekend(date) {
    const day = date.getDay();

    if (day === 0) date.setDate(date.getDate() + 1);
    else if (day === 6) date.setDate(date.getDate() + 2);
  }

  /**
   * @param {Date} date
   * @returns {string}
   */
  #formatDate(date) {
    if (!this.#dateFormatter) {
      this.#dateFormatter = new Intl.DateTimeFormat(document.documentElement.lang || undefined, {
        month: 'short',
        day: 'numeric',
      });
    }

    return this.#dateFormatter.format(date);
  }

  /**
   * @param {string} template
   * @param {Record<string, string>} values
   * @returns {string}
   */
  #fillTemplate(template, values) {
    let result = template;

    for (const [key, value] of Object.entries(values)) {
      result = result.split(`{{ ${key} }}`).join(this.#escapeHtml(value));
    }

    return result;
  }

  /**
   * @param {string} value
   * @returns {string}
   */
  #escapeHtml(value) {
    return value
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  /**
   * @param {string} key
   * @returns {string}
   */
  #getMessage(key) {
    if (!this.#messages) {
      try {
        this.#messages = JSON.parse(this.dataset.messages || '{}');
      } catch {
        this.#messages = {};
      }
    }

    const value = this.#messages?.[key];
    return typeof value === 'string' ? value : '';
  }
}

if (!customElements.get('product-delivery-estimate')) {
  customElements.define('product-delivery-estimate', ProductDeliveryEstimate);
}
